import React, { useMemo } from "react";
import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { DatePicker, DatePickerProps } from "@fluentui/react-datepicker-compat";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { CalendarStrings } from "@fluentui/react-calendar-compat";

const useStyles = makeStyles({
    datePicker: {
        "&&&": {
            "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        },
    },
    datePickerHover: {
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
});

export interface NewformaDatePickerProps
    extends Pick<
        DatePickerProps,
        "value" | "onSelectDate" | "minDate" | "maxDate" | "disabled" | "placeholder" | "className"
    > {
    "data-testid"?: string;
    "aria-label"?: string;
}

const MONTHS = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
] as const;

const DAYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"] as const;

const NewformaDatePickerInner: React.FC<NewformaDatePickerProps & LocalizeContextProps> = (props) => {
    const styles = useStyles();
    const isDarkMode = document.querySelector("[data-theme]")?.getAttribute("data-theme") !== "default";
    const { translate } = props;

    const calendarStrings: CalendarStrings = useMemo(
        () => ({
            months: MONTHS.map((m) => translate(`DATES.MONTHS.${m}`) as string),
            shortMonths: MONTHS.map((m) => translate(`DATES.MONTHS_SHORT.${m}`) as string),
            days: DAYS.map((d) => translate(`DATES.DAYS.${d}`) as string),
            shortDays: DAYS.map((d) => translate(`DATES.DAYS_SHORT.${d}`) as string),
            goToToday: translate("DATES.GO_TO_TODAY") as string,
        }),
        [translate]
    );

    const formatPickerDate = (date?: Date): string => {
        if (!date) return "";
        const day = date.getDate();
        const month = calendarStrings.shortMonths[date.getMonth()];
        const year = date.getFullYear();
        return `${month} ${day}, ${year}`;
    };

    return (
        <DatePicker
            data-testid={props["data-testid"]}
            className={mergeClasses(styles.datePicker, !props.disabled && styles.datePickerHover, props.className)}
            appearance={isDarkMode ? "filled-darker" : "outline"}
            value={props.value ?? null}
            onSelectDate={props.onSelectDate}
            minDate={props.minDate}
            maxDate={props.maxDate}
            disabled={props.disabled}
            placeholder={props.placeholder || (translate("DATES.SELECT_DATE") as string)}
            aria-label={props["aria-label"]}
            showGoToToday={false}
            showCloseButton={false}
            showMonthPickerAsOverlay
            formatDate={formatPickerDate}
            strings={calendarStrings}
        />
    );
};

export const NewformaDatePicker = withLocalize(NewformaDatePickerInner) as React.ComponentType<NewformaDatePickerProps>;
